import { z } from 'zod';
/**
 * Metadata for error message interpolation
 * Used to inject dynamic values into i18n error messages
 *
 * @example
 * { abbreviation: 'TEST', count: 5 }
 * For message: "Department with abbreviation '{{abbreviation}}' already exists"
 *
 * String arrays carry every offending value of a bulk import (e.g. all
 * duplicate emails in one upload).
 */ export const ErrorMetadataSchema = z.record(z.string(), z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.array(z.string())
]));
/**
 * Base Error Schema
 * Standard error response format used across all API endpoints
 *
 * Supports:
 * - HTTP status codes
 * - I18n error messages (keys or plain text)
 * - Field-level validation errors
 * - Metadata for message interpolation
 */ export const BaseErrorSchema = z.object({
    /**
   * HTTP status code (400, 404, 500, etc.)
   */ statusCode: z.number(),
    /**
   * Error message
   * - Can be an i18n key (e.g., 'errors.department.duplicateAbbreviation')
   * - Or a plain text message for backwards compatibility
   */ message: z.string(),
    /**
   * HTTP error name (e.g., 'Bad Request', 'Not Found')
   * Absent on Nest's default body for non-HttpException failures.
   */ error: z.string().optional(),
    /**
   * Field-level validation errors
   * Maps field names to arrays of error messages/keys
   *
   * @example
   * { "email": ["errors.validation.required"], "password": ["errors.validation.minLength"] }
   */ errors: z.record(z.string(), z.array(z.string())).optional(),
    /**
   * Zod validation failures, as emitted by the global validation pipe.
   * Loose on purpose: nothing reads the issue shape beyond `message`.
   */ issues: z.array(z.object({
        message: z.string()
    }).loose()).optional(),
    metadata: ErrorMetadataSchema.optional()
});

//# sourceMappingURL=error.schema.js.map